import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { IMG } from '../../views/landing/landingData';
import { Icons, type Theme } from './studioUi';

interface StudioAccountMenuProps {
    theme: Theme;
    onTheme: (t: Theme) => void;
    /** mailto: for "Help & support"; the item is hidden when omitted. */
    helpHref?: string;
}

/**
 * Avatar menu shared by the website and video studios (2026-11 studio design):
 * Back to dashboard, Profile & billing, Help & support, Theme ▸ Light/Dark, Log out.
 * Opens on click, also on hover for mouse users; the Theme submenu flies out left.
 */
export const StudioAccountMenu: React.FC<StudioAccountMenuProps> = ({ theme, onTheme, helpHref }) => {
    const navigate = useNavigate();
    const { user, logout } = useAuth();
    const [open, setOpen] = useState(false);
    const [themeOpen, setThemeOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const leaveTimer = useRef<number>();

    useEffect(() => {
        if (!open) { setThemeOpen(false); return; }
        const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
        const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDown, true);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown, true); document.removeEventListener('keydown', onKey); };
    }, [open]);
    useEffect(() => () => window.clearTimeout(leaveTimer.current), []);

    const item: React.CSSProperties = {
        width: '100%', display: 'flex', alignItems: 'center', gap: 9, padding: '9px 10px', border: 0, borderRadius: 5, background: 'transparent',
        fontSize: 13.5, fontWeight: 500, color: 'var(--hv-ink)', cursor: 'pointer', textAlign: 'left', textDecoration: 'none', fontFamily: 'inherit',
    };
    const panel: React.CSSProperties = {
        padding: 6, borderRadius: 8, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 24px 44px -20px var(--hv-shadow)', animation: 'hvRise 0.18s ease both',
    };
    const go = (to: string) => { setOpen(false); navigate(to); };
    const check = <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--hv-mint-ink)" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7" /></svg>;

    return (
        <div
            ref={ref}
            style={{ position: 'relative', display: 'flex' }}
            onMouseEnter={() => window.clearTimeout(leaveTimer.current)}
            onMouseLeave={() => { if (open) leaveTimer.current = window.setTimeout(() => setOpen(false), 250); }}
        >
            <button type="button" onClick={() => setOpen((v) => !v)} aria-haspopup="menu" aria-expanded={open} aria-label="Account menu" style={{ width: 36, height: 36, padding: 0, border: 0, borderRadius: 999, background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <img src={user?.avatar || IMG.avatarDefault} alt="" style={{ width: 32, height: 32, borderRadius: 999, objectFit: 'cover', border: '1px solid var(--hv-line)' }} />
            </button>
            {open && (
                <div role="menu" style={{ ...panel, position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 95, width: 220 }}>
                    <button type="button" role="menuitem" className="hv-h-mint" style={item} onClick={() => go('/dashboard')}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M4 11.5 12 5l8 6.5V19a1.5 1.5 0 0 1-1.5 1.5H15v-5h-6v5H5.5A1.5 1.5 0 0 1 4 19z" /></svg>
                        Back to dashboard
                    </button>
                    <button type="button" role="menuitem" className="hv-h-mint" style={item} onClick={() => go('/account/profile')}>{Icons.user({})}Profile &amp; billing</button>
                    {helpHref && (
                        <a role="menuitem" href={helpHref} className="hv-h-mint" style={item} onClick={() => setOpen(false)}>{Icons.help({ size: 15 })}Help &amp; support</a>
                    )}
                    <div style={{ position: 'relative' }} onMouseEnter={() => setThemeOpen(true)} onMouseLeave={() => setThemeOpen(false)}>
                        <button type="button" role="menuitem" aria-haspopup="menu" aria-expanded={themeOpen} className="hv-h-mint" style={item} onClick={() => setThemeOpen((v) => !v)}>
                            {Icons.moon({ size: 15, sw: 1.9 })}
                            <span style={{ flex: 1 }}>Theme</span>
                            <span style={{ fontSize: 12, color: 'var(--hv-muted)' }}>{theme === 'dark' ? 'Dark' : 'Light'}</span>
                            {Icons.chevronR({ size: 13 })}
                        </button>
                        {themeOpen && (
                            <div role="menu" style={{ ...panel, position: 'absolute', right: 'calc(100% + 10px)', top: -6, width: 160, animation: 'hvRise 0.16s ease both' }}>
                                <button type="button" role="menuitemradio" aria-checked={theme === 'light'} className="hv-h-mint" style={item} onClick={() => onTheme('light')}>
                                    {Icons.sun({ size: 15, sw: 1.9 })}<span style={{ flex: 1 }}>Light</span>{theme === 'light' && check}
                                </button>
                                <button type="button" role="menuitemradio" aria-checked={theme === 'dark'} className="hv-h-mint" style={item} onClick={() => onTheme('dark')}>
                                    {Icons.moon({ size: 15, sw: 1.9 })}<span style={{ flex: 1 }}>Dark</span>{theme === 'dark' && check}
                                </button>
                            </div>
                        )}
                    </div>
                    <div style={{ height: 1, margin: '4px 6px', background: 'var(--hv-line)' }} />
                    <button type="button" role="menuitem" className="hv-h-danger-bg" style={{ ...item, color: 'var(--hv-danger)' }} onClick={() => { setOpen(false); logout(); navigate('/'); }}>
                        {Icons.logout({})}Log out
                    </button>
                </div>
            )}
        </div>
    );
};
